import React, { useState, useCallback } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import {
  Box,
  Collapse,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { experimentalStyled as styled } from '@mui/material/styles';
import HeaderBreadcrumbs from './HeaderBreadcrumbs';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Z_INDEX } from '../constants/zIndex';
import HeaderIcons from './HeaderIcons';
import HeaderTour from './Header/HeaderTour';
import useAuth from '../hooks/useAuth';
import { useEventTracking } from 'src/hooks/useEventTracking';

// Types
interface TabItem {
  value: string;
  /**
   * What the tab reads as. Optional and additive: every existing caller passes a
   * dash-separated identifier (`'all-units'`) and gets the historical
   * `value.replaceAll('-', ' ')` label. A caller whose `value` is a route path
   * (the Help and Resources hub) passes this instead, so the identity stays the path
   * and the label stays a word.
   */
  label?: string;
  icon?: React.ReactElement;
  disabled?: boolean;
}

interface LinkItem {
  name: string;
  href?: string;
}

interface UnifiedHeaderProps {
  // Basic props
  heading: string;
  links?: LinkItem[];
  children?: React.ReactNode;
  newBreadCrumbs?: boolean;

  // Tabs functionality
  tabList?: TabItem[];
  currentTab?: string;
  handleChangeTab?: (event: React.SyntheticEvent, newValue: string) => void;
  handleRightClick?: (event: React.MouseEvent, value: string) => void;
  onClick?: (event: React.MouseEvent, value: string) => void;

  // Action buttons
  showSettingsButton?: boolean;
  showHelpButton?: boolean;
  showRefreshButton?: boolean;
  onSettingsClick?: () => void;
  onRefreshClick?: () => void;
  isRefreshing?: boolean;

  // Help content
  helpContent?: React.ReactNode;

  // Tour functionality
  showTourButton?: boolean;
  TourButtonComponent?: React.ComponentType;
  showHeaderTourButton?: boolean;
  HeaderTourButtonComponent?: React.ComponentType;

  // Custom styling
  customHeight?: string;
  showAccentLine?: boolean;

  /**
   * Render the tab row in flow below the content block, instead of absolutely at
   * the header's bottom edge (PA-27). Opt-in: `customHeight` is ignored when it is
   * set, because the header must grow to hold the row rather than clip it.
   */
  tabsInFlow?: boolean;

  // Children positioning (for CertificationConfigHeader style)
  childrenAsContent?: boolean;

  // Page context for tracking
  pageContext?: string;
}

// ----------------------------------------------------------------------

const TabsWrapperStyle = styled('div')(({ theme }) => ({
  zIndex: Z_INDEX.HEADER + 1,
  bottom: 0,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.background.neutral}`,
  [theme.breakpoints.up('sm')]: {
    justifyContent: 'center',
  },
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-start',
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3),
  },
}));

/**
 * The same tab row, in flow below the content block instead of pinned to the
 * header's bottom edge (PA-27).
 *
 * `TabsWrapperStyle` is only correct when the content block leaves room for a 48 px
 * tab row. The Assistant hub's header is a fixed 100 px and its title block already
 * consumes 62 of them, so the absolute row overflowed *up* into the title and *down*
 * onto the border. Property Home never hits this because its row is in flow and the
 * header grows to hold it; this is that behaviour, opt-in.
 *
 * Right-aligned per DD-015, which the absolute row gets wrong at `md` and up.
 */
const TabsInFlowStyle = styled('div')(({ theme }) => ({
  zIndex: Z_INDEX.HEADER + 1,
  width: '100%',
  display: 'flex',
  position: 'relative',
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.background.neutral}`,
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-end',
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3),
  },
}));

// The header sits in normal page flow: it is not fixed and not sticky. It is
// `relative` so it is the containing block for the tab row it renders itself
// (`TabsWrapperStyle` is absolute at `bottom: 0`) — ENG-4211.
//
// It must carry NO `top`/`left` offset. An offset on an in-flow element paints
// the box outside its layout slot, so a `top` pushes the header down over the
// content that follows it — that is the squashed Property Home / My Work header
// when this was `position: relative; top: 25px` (ENG-4230). The old fixed-header
// `&:after` white band went with it: there is nothing scrolling underneath.
const CustomHeader = styled('div')(() => ({
  position: 'relative',
  zIndex: Z_INDEX.CONTENT,
  width: '100%',
}));

// ----------------------------------------------------------------------

const UnifiedHeader: React.FC<UnifiedHeaderProps> = ({
  // Basic props
  heading,
  links = [],
  children = null,
  newBreadCrumbs = false,

  // Tabs functionality
  tabList = [],
  currentTab = '',
  handleChangeTab = () => null,
  handleRightClick = () => null,
  onClick,

  // Action buttons
  showSettingsButton = false,
  showHelpButton = false,
  showRefreshButton = false,
  onSettingsClick = () => null,
  onRefreshClick = () => null,
  isRefreshing = false,

  // Help content
  helpContent = null,

  // Tour functionality
  showTourButton = false,
  TourButtonComponent = null,
  showHeaderTourButton = false,
  HeaderTourButtonComponent = null,

  // Custom styling
  customHeight = '100px',
  showAccentLine = true,
  tabsInFlow = false,

  // Children positioning (for CertificationConfigHeader style)
  childrenAsContent = false,

  // Page context for tracking
  pageContext = 'user_settings',
}: UnifiedHeaderProps): React.JSX.Element => {
  const { user } = useAuth();
  const { width, ref } = useResizeDetector();
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const { trackHeaderHelpClick, trackHeaderTourClick } = useEventTracking();

  const handleHelpClick = useCallback(() => {
    trackHeaderHelpClick(pageContext, 'UnifiedHeader', 'handleHelpClick');
    setShowHelp(!showHelp);
  }, [showHelp, trackHeaderHelpClick, pageContext]);

  const _handleTourClick = useCallback(() => {
    trackHeaderTourClick(pageContext, 'UnifiedHeader', 'handleTourClick');
  }, [trackHeaderTourClick, pageContext]);

  // Determine if we should show any action buttons
  const hasActionButtons =
    showSettingsButton ||
    showHelpButton ||
    showRefreshButton ||
    showTourButton ||
    showHeaderTourButton;

  // PA-27 — the row's own contiguity is the behaviour: in flow it must sit below
  // the header surface, absolutely it must sit on its bottom edge.
  const TabsWrapper = tabsInFlow ? TabsInFlowStyle : TabsWrapperStyle;

  return (
    <>
      <Box ref={ref} />
      <CustomHeader data-testid="unified-header" style={{ width }}>
        {/* Enhanced Unified Header - matching StickyHeader style */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            boxShadow:
              '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.12)',
            backdropFilter: 'blur(8px)',
          }}
          data-tour="settings-header"
        >
          {/* Main content area */}
          <Box
            sx={{
              // PA-27 — an in-flow tab row must be outside the fixed content height,
              // so the header grows instead of the row being clipped. The fixed
              // height stays the default for every other caller.
              height: tabsInFlow
                ? 'auto'
                : childrenAsContent
                ? '100%'
                : customHeight,
              minHeight: tabsInFlow ? customHeight : undefined,
              position: 'relative',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header text content - only show if not using children as content */}
            {!childrenAsContent && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  px: 3,
                  py: 2,
                  position: 'relative',
                  zIndex: 500000,
                }}
              >
                {/* Subtle accent line */}
                {showAccentLine && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '4px',
                      height: '60%',
                      background:
                        'linear-gradient(180deg, #00AB55 0%, #007B55 100%)',
                      borderRadius: '0 2px 2px 0',
                      marginLeft: '24px',
                      zIndex: 1,
                    }}
                  />
                )}

                <Box
                  sx={{ flex: 1, ml: 3, position: 'relative', zIndex: 500000 }}
                >
                  <Typography
                    variant="h4"
                    data-tour="tour-header"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 700,
                      mb: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '1.75rem',
                      letterSpacing: '-0.025em',
                      userSelect: 'text', // Ensure text can be selected
                      WebkitUserSelect: 'text', // Webkit support
                      MozUserSelect: 'text', // IE support
                      position: 'relative',
                      zIndex: 500000,
                    }}
                  >
                    {heading}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <HeaderBreadcrumbs
                      heading=""
                      links={links}
                      sx={{ mb: 0, display: 'flex', alignItems: 'center' }}
                      newBreadCrumbs={newBreadCrumbs}
                    />

                    {/* Action buttons */}
                    {hasActionButtons && (
                      <>
                        {showSettingsButton && (
                          <Tooltip title="Adjust settings" arrow>
                            <IconButton
                              size="small"
                              onClick={onSettingsClick}
                              sx={{
                                ml: 1,
                                alignSelf: 'flex-start',
                                mt: '2px',
                                p: 0.5,
                              }}
                              data-tour="settings-icon"
                            >
                              <SettingsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {showHelpButton && (
                          <Tooltip title="Help">
                            <IconButton
                              size="small"
                              onClick={handleHelpClick}
                              sx={{
                                ml: 1,
                                alignSelf: 'flex-start',
                                mt: '2px',
                                p: 0.5,
                              }}
                              data-tour="help-icon"
                            >
                              <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {showRefreshButton && (
                          <Tooltip title="Refresh" arrow>
                            <IconButton
                              size="small"
                              onClick={onRefreshClick}
                              disabled={isRefreshing}
                              sx={{
                                ml: 1,
                                alignSelf: 'flex-start',
                                mt: '2px',
                                p: 0.5,
                              }}
                              data-tour="refresh-icon"
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {showTourButton && TourButtonComponent && (
                          <TourButtonComponent user={user} />
                        )}

                        {showHeaderTourButton && HeaderTourButtonComponent && (
                          <HeaderTourButtonComponent user={user} />
                        )}
                      </>
                    )}
                  </Box>
                </Box>

                {/* Right side content for children */}
                {children && (
                  <Box sx={{ flexShrink: 0, ml: 2 }}>{children}</Box>
                )}
              </Box>
            )}

            {/* Children as content (for CertificationConfigHeader style) */}
            {childrenAsContent && children && (
              <Box sx={{ height: '100%', width: '100%' }}>{children}</Box>
            )}
          </Box>

          {/* Help content */}
          {showHelp && helpContent && (
            <Collapse in={showHelp}>
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.neutral',
                }}
              >
                {helpContent}
              </Box>
            </Collapse>
          )}
        </Box>

        {/* Tabs */}
        {tabList?.length > 0 && (
          <TabsWrapper
            data-testid="unified-header-tabs"
            data-in-flow={tabsInFlow ? 'true' : 'false'}
          >
            <Tabs
              value={currentTab}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={handleChangeTab}
            >
              {tabList.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label ?? tab.value.replaceAll('-', ' ')}
                  icon={tab.icon}
                  iconPosition="start"
                  disabled={tab.disabled}
                  onContextMenu={(e) => handleRightClick(e, tab.value)}
                  onClick={(e) => {
                    // ENG-4220 — fire for every tab, not only a single-tab header.
                    // MUI does not call `onChange` when the clicked tab is already
                    // selected, which is exactly the case that needs it: on
                    // `/hub/cases/42` the Assistant tab *is* selected, and clicking
                    // it has to take the user back to the list. Callers that pass no
                    // `onClick` are unaffected (verified: only SupportHubShell does).
                    onClick?.(e, tab.value);
                  }}
                  disableRipple
                />
              ))}
            </Tabs>
          </TabsWrapper>
        )}
      </CustomHeader>

      {/* Header Icons positioned above the header */}
      <HeaderTour user={user}>
        <HeaderIcons position="above" />
      </HeaderTour>
    </>
  );
};

export default UnifiedHeader;
